/*
 * ARK Shop All — behavior for sections/ark-shop-all.liquid
 *
 * - Category views with shareable links (?category=cat-hats) filtering one best-selling grid
 * - Sorting (?sort=price-asc) without reloading
 * - Swipeable card images (hover to preview on desktop)
 * - Quick view sheet with colorways, bundle picks and add to cart (?product=handle)
 * - Shop Pay / express checkout buttons and product-view tracking (Meta, Klaviyo) in the quick view
 * - AJAX add to cart that keeps the theme cart icon/drawer in sync
 * - Sticky checkout bar with free shipping progress
 *
 * No dependencies. Everything is delegated from the section root.
 */
(() => {
  'use strict';

  const instances = new Map();

  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  /** Short text for the sort button once a sort is picked. */
  const SORT_LABELS = {
    featured: 'Sort',
    'best-selling': 'Best selling',
    'title-asc': 'A\u2013Z',
    'title-desc': 'Z\u2013A',
    'price-asc': 'Price \u2191',
    'price-desc': 'Price \u2193',
    'date-asc': 'Oldest',
    'date-desc': 'Newest',
  };

  /** Older links and Shopify's own sort_by values keep working. */
  const SORT_ALIASES = {
    newest: 'date-desc',
    oldest: 'date-asc',
    'best-selling': 'best-selling',
    manual: 'featured',
    'title-ascending': 'title-asc',
    'title-descending': 'title-desc',
    'price-ascending': 'price-asc',
    'price-descending': 'price-desc',
    'created-ascending': 'date-asc',
    'created-descending': 'date-desc',
  };

  const readJSON = (el) => {
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (error) {
      return null;
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isWideQuickView = () => window.matchMedia('(min-width: 900px)').matches;

  const imgUrl = (src, width) => {
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };

  const srcset = (src, widths) => widths.map((w) => `${imgUrl(src, w)} ${w}w`).join(', ');

  /** "Pine Green Crochet Cat Hat" -> { name: "Pine Green", family: "cat hat" } */
  const titleParts = (title) => {
    const text = String(title || '');
    const match = text.match(/^(.*?)\s+crochet\s+(.+)$/i);
    if (!match) return { name: text, family: '' };
    const paren = (match[2].match(/\(([^)]+)\)/) || [])[1];
    const family = match[2].replace(/\([^)]*\)/g, '').trim().toLowerCase();
    return { name: paren ? `${match[1]} (${paren})` : match[1], family };
  };

  const orderImages = (images, leadModel) => {
    if (!leadModel || images.length < 2) return images.slice();
    return [images[1], images[0], ...images.slice(2)];
  };

  const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

  /* ---------- Shop All ---------- */

  class ShopAll {
    constructor(root) {
      this.root = root;
      this.cfg = readJSON(root.querySelector('[data-sa-config]')) || {};
      this.cfg.routes = this.cfg.routes || {};
      this.data = null;
      this.fetched = new Map();
      this.cart = null;
      this.sortMode = 'featured';
      this.viewed = new Set();
      this.expressCache = new Map();
      this.picks = new Map(); // bundle picks toggled on in the open quick view
      this.pickList = [];
      this.qvAdded = false;
      this.qvOpen = false;
      this.qvProduct = null;
      this.qvVariant = null;
      this.frames = new Map();
      this.abort = new AbortController();

      this.rail = root.querySelector('[data-sa-rail]');
      this.railScroller = root.querySelector('[data-sa-rail-scroller]');
      this.sortSelect = root.querySelector('[data-sa-sort-select]');
      this.sortLabel = root.querySelector('[data-sa-sort-label]');
      this.live = root.querySelector('[data-sa-live]');
      this.bar = root.querySelector('[data-sa-bar]');

      const qv = root.querySelector('[data-sa-qv]');
      this.qv = {
        dialog: qv,
        panel: qv.querySelector('[data-sa-qv-panel]'),
        scroll: qv.querySelector('[data-sa-qv-scroll]'),
        track: qv.querySelector('[data-sa-qv-track]'),
        counter: qv.querySelector('[data-sa-qv-counter]'),
        title: qv.querySelector('[data-sa-qv-title]'),
        price: qv.querySelector('[data-sa-qv-price]'),
        stock: qv.querySelector('[data-sa-qv-stock]'),
        colorways: qv.querySelector('[data-sa-qv-colorways]'),
        colorwayName: qv.querySelector('[data-sa-qv-colorway-name]'),
        swatches: qv.querySelector('[data-sa-qv-swatches]'),
        options: qv.querySelector('[data-sa-qv-options]'),
        meter: qv.querySelector('[data-sa-qv-meter]'),
        meterText: qv.querySelector('[data-sa-qv-meter-text]'),
        meterFill: qv.querySelector('[data-sa-qv-meter-fill]'),
        bundle: qv.querySelector('[data-sa-qv-bundle]'),
        bundleList: qv.querySelector('[data-sa-qv-bundle-list]'),
        desc: qv.querySelector('[data-sa-qv-desc]'),
        more: qv.querySelector('[data-sa-qv-more]'),
        pdp: qv.querySelector('[data-sa-qv-pdp]'),
        error: qv.querySelector('[data-sa-qv-error]'),
        foot: qv.querySelector('[data-sa-qv-foot]'),
        qty: qv.querySelector('[data-sa-qty-input]'),
        add: qv.querySelector('[data-sa-qv-add]'),
        addLabel: qv.querySelector('[data-sa-qv-add-label]'),
        checkout: qv.querySelector('[data-sa-qv-foot] [data-sa-checkout]'),
        continue: qv.querySelector('[data-sa-qv-continue]'),
        express: qv.querySelector('[data-sa-qv-express]'),
      };
      this.saleSection = root.querySelector('[data-sa-sale]');

      const rate = parseFloat(window.Shopify?.currency?.rate || '1') || 1;
      this.freeShip = Math.round((Number(this.cfg.freeShip) || 0) * rate);

      try {
        this.money = new Intl.NumberFormat(this.cfg.locale || document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.cfg.currency || window.Shopify?.currency?.active || 'USD',
          currencyDisplay: 'narrowSymbol',
        });
      } catch (error) {
        this.money = { format: (n) => `$${n.toFixed(2)}` };
      }

      this.init();
    }

    /* ---------- setup ---------- */

    init() {
      const { signal } = this.abort;
      const root = this.root;

      root.addEventListener('click', (event) => this.onClick(event), { signal });
      root.addEventListener('pointerover', (event) => this.onPointerOver(event), { signal });
      root.addEventListener('pointerout', (event) => this.onPointerOut(event), { signal });
      root.addEventListener('change', (event) => this.onChange(event), { signal });
      document.addEventListener('scroll', (event) => this.onScroll(event), { capture: true, passive: true, signal });
      root.addEventListener(
        'touchstart',
        (event) => {
          const track = event.target instanceof Element ? event.target.closest('[data-sa-track]') : null;
          if (track) this.hydrateTrack(track);
        },
        { passive: true, signal }
      );
      window.addEventListener('popstate', (event) => this.onPopState(event), { signal });
      window.addEventListener('pageshow', (event) => event.persisted && this.refreshCart(), { signal });
      document.addEventListener('cart:update', (event) => this.onThemeCartUpdate(event), { signal });

      const { dialog } = this.qv;
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeQuickView();
      }, { signal });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeQuickView();
      }, { signal });
      this.bindDrag();

      // Shoppers stay on this page after adding: keep cart drawers from auto-opening on our adds.
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach((el) => el.removeAttribute('auto-open'));
      this.pendingAdds = 0;
      const upcartSkip = window.upcartShouldSkipAddToCart;
      window.upcartShouldSkipAddToCart = (...args) =>
        this.pendingAdds > 0 || (typeof upcartSkip === 'function' ? upcartSkip(...args) : false);

      // The cart app doesn't tell the theme when a shopper edits the cart in its drawer,
      // so the bar and the header count would go stale. Listen to its own hooks instead.
      const onCartAppChange = () => {
        clearTimeout(this.cartTimer);
        this.cartTimer = setTimeout(() => this.refreshCart({ notifyTheme: true }), 200);
      };
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        try {
          window.upcartSubscribeCartUpdated(onCartAppChange);
        } catch (error) {
          /* cart app hooks are best effort */
        }
      } else {
        const previous = window.upcartOnCartUpdated;
        window.upcartOnCartUpdated = (...args) => {
          if (typeof previous === 'function') previous(...args);
          onCartAppChange();
        };
      }
      const previousClosed = window.upcartOnCartClosed;
      window.upcartOnCartClosed = (...args) => {
        if (typeof previousClosed === 'function') previousClosed(...args);
        onCartAppChange();
      };

      // Catch every cart change, whoever makes it (cart drawer app, theme, another script).
      const nativeFetch = window.fetch;
      if (typeof nativeFetch === 'function') {
        window.fetch = function (...args) {
          const result = nativeFetch.apply(this, args);
          try {
            const input = args[0];
            const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
            if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
              Promise.resolve(result).then(onCartAppChange, () => {});
            }
          } catch (error) {
            /* never block the request */
          }
          return result;
        };
      }
      const bodyWatcher = new MutationObserver(() => {
        const open = document.body.classList.contains('upcartPopupShow');
        if (this.cartDrawerOpen && !open) onCartAppChange(); // drawer just closed
        this.cartDrawerOpen = open;
      });
      bodyWatcher.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      signal.addEventListener('abort', () => bodyWatcher.disconnect());

      this.syncHeaderOffset();
      this.syncChips(false);
      this.syncRailFade();
      this.syncSale();

      const params = new URLSearchParams(window.location.search);
      const sort = SORT_ALIASES[params.get('sort')] || params.get('sort');
      if (sort && sort !== 'featured' && this.sortSelect?.querySelector(`option[value="${CSS.escape(sort)}"]`)) {
        this.sortSelect.value = sort;
        this.setSort(sort, { updateUrl: false });
      }

      const handle = params.get('product');
      if (handle) {
        // Give direct product links a history entry so "back" closes the sheet instead of leaving.
        const base = new URL(window.location.href);
        base.searchParams.delete('product');
        history.replaceState(history.state, '', base);
        this.openQuickView(handle, { push: true });
      }

      this.saveReturnUrl();
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 600));
      idle(() => this.refreshCart());
    }

    destroy() {
      this.abort.abort();
      if (this.qvOpen) this.qv.dialog.close();
      document.documentElement.classList.remove('sa-lock');
      document.body.classList.remove('sa-bar-on');
    }

    getData(handle) {
      if (!this.data) {
        this.data = readJSON(this.root.querySelector('[data-sa-products]')) || {};
        this.normalized = new Map();
      }
      if (this.normalized.has(handle)) return this.normalized.get(handle);
      const raw = this.data[handle];
      if (raw) {
        const product = {
          handle,
          id: raw.id,
          title: raw.t,
          url: raw.u,
          description: raw.d || '',
          available: Boolean(raw.a),
          price: raw.p,
          compare: raw.c || 0,
          published: raw.n || 0,
          leadModel: Boolean(raw.m),
          vendor: raw.b || '',
          type: raw.ty || '',
          options: raw.o || [],
          images: raw.i || [],
          variants: (raw.v || []).map((v) => ({
            id: v[0],
            available: Boolean(v[1]),
            price: v[2],
            compare: v[3] || 0,
            options: v[4] || [],
            qty: typeof v[5] === 'number' ? v[5] : null,
          })),
        };
        this.normalized.set(handle, product);
        return product;
      }
      return this.fetched.get(handle) || null;
    }

    async fetchProduct(handle) {
      const cached = this.getData(handle);
      if (cached) return cached;
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`Product ${handle} not found`);
      const p = await res.json();
      const product = {
        handle: p.handle,
        id: p.id,
        title: p.title,
        url: p.url || `/products/${p.handle}`,
        description: p.description || '',
        available: Boolean(p.available),
        price: p.price,
        compare: p.compare_at_price || 0,
        published: Math.floor(Date.parse(p.published_at || p.created_at || 0) / 1000) || 0,
        vendor: p.vendor || '',
        type: p.type || '',
        leadModel: Boolean(this.cfg.leadModel && p.media?.length > 1 && Math.abs((p.media[0].aspect_ratio || 0) - 1) < 0.05),
        options: (p.options || []).map((o) => (typeof o === 'string' ? o : o.name)),
        images: p.images || [],
        variants: (p.variants || []).map((v) => ({
          id: v.id,
          available: Boolean(v.available),
          price: v.price,
          compare: v.compare_at_price || 0,
          options: v.options || [],
          qty: null,
        })),
      };
      this.fetched.set(handle, product);
      return product;
    }

    format(cents) {
      return this.money.format((Number(cents) || 0) / 100);
    }

    announce(message) {
      if (!this.live) return;
      this.live.textContent = '';
      requestAnimationFrame(() => {
        this.live.textContent = message;
      });
    }

    track(name, payload) {
      try {
        window.Shopify?.analytics?.publish?.(name, payload);
      } catch (error) {
        /* analytics is best effort */
      }
    }

    frame(key, fn) {
      if (this.frames.has(key)) return;
      this.frames.set(
        key,
        requestAnimationFrame(() => {
          this.frames.delete(key);
          fn();
        })
      );
    }

    /* Product pages use this to send shoppers back to the exact view they left. */
    saveReturnUrl() {
      try {
        // Only category + sort: never carry ad params (utm_*, fbclid) into internal links.
        const current = new URL(window.location.href);
        const keep = new URLSearchParams();
        ['category', 'sort'].forEach((key) => {
          const value = current.searchParams.get(key);
          if (value) keep.set(key, value);
        });
        const query = keep.toString();
        sessionStorage.setItem('ark-shop-all-return', JSON.stringify({ url: current.pathname + (query ? `?${query}` : ''), t: Date.now() }));
      } catch (error) {
        /* ignore storage errors */
      }
    }

    /* The layout measures the header group before this section's CSS hides the nav chips. */
    syncHeaderOffset() {
      const header = document.querySelector('header-component');
      const group = document.querySelector('#header-group');
      if (!header || !group) return;
      let height = 0;
      for (const el of group.children) {
        if (el === header || !(el instanceof HTMLElement)) continue;
        height += el.offsetHeight;
      }
      document.body.style.setProperty('--header-group-height', `${height}px`);
      document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;

      const chip = target.closest('[data-sa-chip]');
      if (chip && this.root.contains(chip)) {
        if (modified) return;
        event.preventDefault();
        this.setView(chip.dataset.saChip, { scroll: true });
        return;
      }

      const quickAdd = target.closest('[data-sa-add]');
      if (quickAdd && this.root.contains(quickAdd)) {
        event.preventDefault();
        this.quickAdd(quickAdd);
        return;
      }

      const step = target.closest('[data-sa-step]');
      if (step) {
        event.preventDefault();
        const track = step.closest('.sa-card__media')?.querySelector('[data-sa-track]');
        if (track) this.stepTrack(track, Number(step.dataset.saStep));
        return;
      }

      const open = target.closest('[data-sa-open]');
      if (open && !this.qv.dialog.contains(open)) {
        if (modified) return;
        const card = open.closest('[data-sa-card]');
        if (!card) return;
        event.preventDefault();
        this.openQuickView(card.dataset.handle, { card, push: true });
        return;
      }

      if (!this.qv.dialog.contains(target) && !target.closest('[data-sa-bar]')) return;

      if (target.closest('[data-sa-qv-close]')) {
        this.closeQuickView();
      } else if (target.closest('[data-sa-qv-step]')) {
        const dir = Number(target.closest('[data-sa-qv-step]').dataset.saQvStep);
        this.stepTrack(this.qv.track, dir);
      } else if (target.closest('[data-sa-swatch]')) {
        this.switchProduct(target.closest('[data-sa-swatch]').dataset.saSwatch);
      } else if (target.closest('[data-sa-bundle-view]')) {
        this.switchProduct(target.closest('[data-sa-bundle-view]').dataset.saBundleView);
      } else if (target.closest('[data-sa-bundle-toggle]')) {
        this.toggleBundlePick(target.closest('[data-sa-bundle-toggle]'));
      } else if (target.closest('[data-sa-qv-continue]')) {
        this.track('ark_shop_all_continue', { handle: this.qvProduct?.handle });
        this.closeQuickView();
      } else if (target.closest('[data-sa-opt]')) {
        this.selectOption(target.closest('[data-sa-opt]'));
      } else if (target.closest('[data-sa-qty]')) {
        const input = this.qv.qty;
        input.value = clamp((parseInt(input.value, 10) || 1) + Number(target.closest('[data-sa-qty]').dataset.saQty), 1, 99);
        this.updateCta();
        this.syncExpressQuantity();
      } else if (target.closest('[data-sa-qv-add]')) {
        this.addFromQuickView();
      } else if (target.closest('[data-sa-qv-more]')) {
        this.qv.desc.classList.remove('is-clamped');
        this.qv.more.hidden = true;
      } else if (target.closest('[data-sa-qv-pdp]')) {
        this.track('ark_shop_all_view_product_page', { handle: this.qvProduct?.handle });
      } else if (target.closest('[data-sa-bar-cart]')) {
        this.openCart();
      } else if (target.closest('[data-sa-checkout]')) {
        this.track('ark_shop_all_checkout', { itemCount: this.cart?.item_count || 0, source: target.closest('[data-sa-bar]') ? 'bar' : 'quick_view' });
      }
    }

    quickAdd(button) {
      const id = Number(button.dataset.saAdd);
      if (!id || button.classList.contains('is-busy')) return;
      const card = button.closest('[data-sa-card]');
      this.setButtonState(button, 'busy');
      this.addToCart([{ id, quantity: 1 }])
        .then(() => {
          this.setButtonState(button, 'done', 'Added');
          this.announce(`${this.getData(card?.dataset.handle)?.title || 'Item'} added to cart`);
          this.track('ark_shop_all_quick_add', { handle: card?.dataset.handle, variantId: id, source: 'card' });
        })
        .catch((error) => this.setButtonState(button, 'error', error.userMessage || 'Unavailable'));
    }

    onChange(event) {
      if (event.target === this.sortSelect) {
        this.setSort(this.sortSelect.value);
      } else if (event.target === this.qv.qty) {
        this.qv.qty.value = clamp(parseInt(this.qv.qty.value, 10) || 1, 1, 99);
        this.updateCta();
        this.syncExpressQuantity();
      }
    }

    onScroll(event) {
      const el = event.target;
      if (!(el instanceof Element)) return;
      if (el.matches('[data-sa-track]')) {
        this.hydrateTrack(el);
        this.frame(el, () => this.syncDots(el));
      }
      else if (el === this.qv.track) this.frame(el, () => this.syncCounter());
      else if (el === this.railScroller) this.frame(el, () => this.syncRailFade());
    }

    onPointerOver(event) {
      if (event.pointerType !== 'mouse') return;
      const media = event.target instanceof Element ? event.target.closest('.sa-card__media') : null;
      if (!media || media.contains(event.relatedTarget)) return;
      const track = media.querySelector('[data-sa-track]');
      if (!track || track.children.length < 2) return;
      this.hydrateTrack(track);
      if (media.dataset.saTouched) return;
      if (Math.round(track.scrollLeft) === 0) this.scrollTrack(track, 1);
    }

    onPointerOut(event) {
      if (event.pointerType !== 'mouse') return;
      const media = event.target instanceof Element ? event.target.closest('.sa-card__media') : null;
      if (!media || media.contains(event.relatedTarget)) return;
      const track = media.querySelector('[data-sa-track]');
      if (track && !media.dataset.saTouched) this.scrollTrack(track, 0);
    }

    onPopState(event) {
      const handle = new URL(window.location.href).searchParams.get('product');
      if (event.state && event.state.saQv && handle) {
        this.openQuickView(handle, { push: false });
      } else if (this.qvOpen) {
        this.closeQuickView({ fromHistory: true });
      }
    }

    onThemeCartUpdate(event) {
      if (event.detail?.sourceId === 'ark-shop-all') return;
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => this.refreshCart(), 250);
    }

    /* ---------- category views ---------- */

    setView(slug, { scroll = false } = {}) {
      slug = (this.cfg.aliases && this.cfg.aliases[slug]) || slug;
      const exists = this.root.querySelector(`.sa-chip[data-sa-chip="${CSS.escape(slug)}"]`);
      if (!exists) return;
      const changed = this.root.dataset.view !== slug;
      this.root.dataset.view = slug;
      this.syncChips(true);
      this.syncSale();

      const url = new URL(window.location.href);
      if (slug === 'all') url.searchParams.delete('category');
      else url.searchParams.set('category', slug);
      url.hash = '';
      history.replaceState(history.state, '', url);
      this.saveReturnUrl();

      if (scroll) {
        const secs = this.root.querySelector('[data-sa-secs]');
        const railBottom = this.rail.getBoundingClientRect().bottom;
        const top = secs.getBoundingClientRect().top;
        if (top < railBottom - 1) {
          window.scrollTo({ top: window.scrollY + top - railBottom, behavior: 'auto' });
        }
      }

      if (changed) {
        const label = this.root.querySelector(`.sa-chip[data-sa-chip="${CSS.escape(slug)}"] .sa-chip__label`)?.textContent || slug;
        this.announce(`Showing ${label}`);
        this.track('ark_shop_all_category', { category: slug });
      }
    }

    syncChips(smooth) {
      const view = this.root.dataset.view;
      let active = null;
      this.root.querySelectorAll('.sa-chip[data-sa-chip]').forEach((chip) => {
        const on = chip.dataset.saChip === view;
        if (on) {
          chip.setAttribute('aria-current', 'true');
          active = chip;
        } else {
          chip.removeAttribute('aria-current');
        }
      });
      if (active && this.railScroller) {
        const scroller = this.railScroller;
        const left = active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
        scroller.scrollTo({ left: Math.max(0, left), behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
      }
    }

    syncRailFade() {
      const s = this.railScroller;
      if (!s) return;
      s.classList.toggle('is-start', s.scrollLeft <= 4);
      s.classList.toggle('is-end', s.scrollLeft + s.clientWidth >= s.scrollWidth - 4);
    }

    /* Hide the Sale block in category views when none of its items belong to that category. */
    syncSale() {
      const sale = this.saleSection;
      if (!sale) return;
      sale.hidden = false;
      const view = this.root.dataset.view;
      if (view === 'all' || getComputedStyle(sale).display === 'none') return;
      const visible = Array.from(sale.querySelectorAll('[data-sa-card]')).some((card) => getComputedStyle(card).display !== 'none');
      sale.hidden = !visible;
    }

    /* ---------- sorting ---------- */

    setSort(mode, { updateUrl = true } = {}) {
      const sortWrap = this.root.querySelector('[data-sa-sort]');
      const option = this.sortSelect?.querySelector(`option[value="${CSS.escape(mode)}"]`);
      this.sortMode = mode;

      if (updateUrl) {
        const url = new URL(window.location.href);
        if (mode === 'featured') url.searchParams.delete('sort');
        else url.searchParams.set('sort', mode);
        history.replaceState(history.state, '', url);
        this.saveReturnUrl();
      }

      sortWrap?.classList.toggle('is-active', mode !== 'featured');
      if (this.sortLabel) this.sortLabel.textContent = SORT_LABELS[mode] || 'Sort';

      const compare = this.comparator(mode);

      this.root.querySelectorAll('[data-sa-grid]').forEach((grid) => {
        const cards = Array.from(grid.querySelectorAll(':scope > [data-sa-card]'));
        cards.forEach((card, i) => {
          if (card.dataset.saIndex === undefined) card.dataset.saIndex = String(i);
        });
        cards.sort(compare);
        const fragment = document.createDocumentFragment();
        cards.forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);
      });

      this.announce(`Sorted by ${option ? option.textContent : mode}`);
      this.track('ark_shop_all_sort', { sort: mode });
    }

    comparator(mode) {
      const byIndex = (a, b) => Number(a.dataset.saIndex) - Number(b.dataset.saIndex);
      const value = (card, key) => {
        const d = this.getData(card.dataset.handle);
        return d ? d[key] : null;
      };
      const soldOutLast = (a, b) => Number(a.classList.contains('is-soldout')) - Number(b.classList.contains('is-soldout'));
      // Products outside the Best selling collection have rank 0; they sort after the ranked ones.
      const bestRank = (card) => Number(card.dataset.best) || Number.MAX_SAFE_INTEGER;

      switch (mode) {
        case 'best-selling':
          return (a, b) => soldOutLast(a, b) || bestRank(a) - bestRank(b) || byIndex(a, b);
        case 'title-asc':
        case 'title-desc': {
          const dir = mode === 'title-asc' ? 1 : -1;
          return (a, b) =>
            soldOutLast(a, b) ||
            dir * collator.compare(value(a, 'title') || '', value(b, 'title') || '') ||
            byIndex(a, b);
        }
        case 'price-asc':
        case 'price-desc': {
          const dir = mode === 'price-asc' ? 1 : -1;
          return (a, b) => soldOutLast(a, b) || dir * ((value(a, 'price') ?? 0) - (value(b, 'price') ?? 0)) || byIndex(a, b);
        }
        case 'date-asc':
        case 'date-desc': {
          const dir = mode === 'date-asc' ? 1 : -1;
          return (a, b) => soldOutLast(a, b) || dir * ((value(a, 'published') || 0) - (value(b, 'published') || 0)) || byIndex(a, b);
        }
        default:
          return byIndex; // server order: pinned, then the drop, then best sellers
      }
    }

    /* ---------- card images ---------- */

    scrollTrack(track, index) {
      const count = track.children.length;
      if (!count) return;
      const i = clamp(index, 0, count - 1);
      if (track !== this.qv.track) this.hydrateTrack(track); // quick view photos hydrate per slide in syncCounter
      track.scrollTo({ left: i * track.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    stepTrack(track, dir) {
      const count = track.children.length;
      if (count < 2) return;
      const current = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const media = track.closest('.sa-card__media');
      if (media) media.dataset.saTouched = '1';
      this.scrollTrack(track, (current + dir + count) % count);
    }

    syncDots(track) {
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (track.saIndex === index) return;
      track.saIndex = index;
      const dots = track.parentElement.querySelector('.sa-card__dots');
      if (!dots) return;
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-on', i === index));
    }

    /* Card photos after the first only download once the shopper touches, hovers or swipes that card. */
    hydrateTrack(track) {
      if (!track || track.saHydrated) return;
      track.saHydrated = true;
      track.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }

    /* ---------- quick view ---------- */

    async openQuickView(handle, { card = null, push = true } = {}) {
      if (!handle) return;
      const { dialog, track } = this.qv;

      let startIndex = 0;
      let placeholder = '';
      const sourceCard = card || this.root.querySelector(`[data-sa-card][data-handle="${CSS.escape(handle)}"]`);
      if (card) {
        const cardTrack = card.querySelector('[data-sa-track]');
        if (cardTrack) {
          startIndex = Math.round(cardTrack.scrollLeft / Math.max(1, cardTrack.clientWidth));
          placeholder = cardTrack.children[startIndex]?.querySelector('img')?.currentSrc || '';
        }
      }

      let product = this.getData(handle);
      if (!product) {
        this.qv.title.textContent = sourceCard?.querySelector('.sa-card__title')?.textContent.trim() || '';
        this.qv.price.textContent = '';
        track.innerHTML = '';
      }

      if (push) {
        const url = new URL(window.location.href);
        url.searchParams.set('product', handle);
        if (this.qvOpen && history.state?.saQv) history.replaceState({ saQv: handle }, '', url);
        else history.pushState({ saQv: handle }, '', url);
      }

      if (!this.qvOpen) {
        this.qvOpen = true;
        this.qvReturnFocus = document.activeElement;
        dialog.classList.remove('is-closing');
        dialog.showModal();
        document.documentElement.classList.add('sa-lock');
      }

      if (product) {
        this.renderQuickView(product, { startIndex, placeholder });
      } else {
        try {
          product = await this.fetchProduct(handle);
          if (this.qvOpen) this.renderQuickView(product, { startIndex: 0 });
        } catch (error) {
          this.closeQuickView();
          if (sourceCard) window.location.href = sourceCard.querySelector('a[href]')?.href || '/';
          return;
        }
      }

      this.track('ark_shop_all_quick_view', {
        handle: product.handle,
        productId: product.id,
        price: product.price / 100,
        currency: this.cfg.currency,
      });
      this.reportProductView(product);
    }

    switchProduct(handle) {
      if (!handle || handle === this.qvProduct?.handle) return;
      this.openQuickView(handle, { push: true });
      this.qv.scroll.scrollTo({ top: 0, behavior: 'auto' });
    }

    closeQuickView({ fromHistory = false } = {}) {
      if (!this.qvOpen) return;
      if (!fromHistory && history.state?.saQv) {
        history.back(); // popstate closes the sheet
        return;
      }
      if (!fromHistory) {
        const url = new URL(window.location.href);
        url.searchParams.delete('product');
        history.replaceState(null, '', url);
      }
      this.qvOpen = false;
      const { dialog, panel } = this.qv;

      const finish = () => {
        dialog.classList.remove('is-closing');
        dialog.close();
        panel.style.transform = '';
        document.documentElement.classList.remove('sa-lock');
        this.qv.track.innerHTML = '';
        const focusTarget = this.qvReturnFocus;
        if (focusTarget && typeof focusTarget.focus === 'function' && document.contains(focusTarget)) {
          focusTarget.focus({ preventScroll: true });
        }
      };

      if (prefersReducedMotion()) {
        finish();
        return;
      }
      dialog.classList.add('is-closing');
      let done = false;
      const end = () => {
        if (done) return;
        done = true;
        finish();
      };
      panel.addEventListener('animationend', end, { once: true });
      setTimeout(end, 320);
    }

    renderQuickView(product, { startIndex = 0, placeholder = '' } = {}) {
      const q = this.qv;
      this.qvProduct = product;

      const images = orderImages(product.images, product.leadModel);
      const total = images.length;
      const start = clamp(startIndex, 0, Math.max(0, total - 1));
      q.track.innerHTML = images
        .map((src, i) => {
          const bg = i === start && placeholder ? ` style="background-image:url('${escapeHtml(placeholder)}')"` : '';
          const near = Math.abs(i - start) <= 1;
          const sources = `src="${escapeHtml(imgUrl(src, 900))}" srcset="${escapeHtml(srcset(src, [480, 750, 1080, 1400]))}"`;
          const attrs = near ? `${sources}${i === start ? ' fetchpriority="high"' : ''}` : sources.replace('src=', 'data-src=').replace('srcset=', 'data-srcset=');
          return `<div class="sa-qv__slide"${bg}><img ${attrs} sizes="(min-width: 900px) 560px, 100vw" alt="${escapeHtml(product.title)}${total > 1 ? `, image ${i + 1} of ${total}` : ''}" decoding="async" draggable="false"></div>`;
        })
        .join('');
      q.track.saIndex = start;
      requestAnimationFrame(() => {
        q.track.scrollLeft = start * q.track.clientWidth;
        this.syncCounter();
      });
      q.counter.hidden = total < 2;
      q.dialog.querySelectorAll('[data-sa-qv-step]').forEach((b) => (b.hidden = total < 2));

      q.title.textContent = product.title;
      q.pdp.href = product.url;

      const defaultOnly =
        product.variants.length <= 1 && (product.options.length === 0 || product.options[0] === 'Title');
      const variant = product.variants.find((v) => v.available) || product.variants[0] || null;
      this.qvVariant = variant;

      // Colorways: products from the same family on this page ("... Crochet Cat Hat")
      const { name, family } = titleParts(product.title);
      const siblings = family ? this.familyHandles(family) : [];
      if (siblings.length > 1) {
        q.colorways.hidden = false;
        q.colorwayName.textContent = name;
        q.swatches.innerHTML = siblings
          .map((handle) => {
            const d = this.getData(handle);
            if (!d) return '';
            const current = handle === product.handle;
            const thumb = d.images[0] ? imgUrl(d.images[0], 140) : '';
            return `<button type="button" class="sa-swatch${d.available ? '' : ' is-soldout'}" data-sa-swatch="${escapeHtml(handle)}" aria-label="${escapeHtml(titleParts(d.title).name)}${d.available ? '' : ' (sold out)'}"${current ? ' aria-current="true"' : ''}>${thumb ? `<img src="${escapeHtml(thumb)}" alt="" loading="lazy" decoding="async" width="60" height="60">` : ''}</button>`;
          })
          .join('');
      } else {
        q.colorways.hidden = true;
        q.swatches.innerHTML = '';
      }

      // Variant options (only for products with real options)
      if (!defaultOnly && product.options.length) {
        q.options.hidden = false;
        q.options.innerHTML = product.options
          .map((optName, oi) => {
            const values = [...new Set(product.variants.map((v) => v.options[oi]))];
            return `<div class="sa-opt"><p class="sa-qv__label">${escapeHtml(optName)}<span data-sa-opt-name="${oi}"></span></p><div class="sa-opt__values">${values
              .map(
                (val) =>
                  `<button type="button" class="sa-opt__value" data-sa-opt="${oi}" data-value="${escapeHtml(val)}" aria-pressed="false">${escapeHtml(val)}</button>`
              )
              .join('')}</div></div>`;
          })
          .join('');
      } else {
        q.options.hidden = true;
        q.options.innerHTML = '';
      }

      // Description
      q.desc.innerHTML = product.description || '';
      q.desc.classList.remove('is-clamped');
      q.more.hidden = true;
      requestAnimationFrame(() => {
        if (q.desc.scrollHeight > 260) {
          q.desc.classList.add('is-clamped');
          q.more.hidden = false;
        }
      });

      // Footer: back to "add" mode for this product
      q.error.hidden = true;
      q.foot.classList.remove('is-added');
      q.checkout.hidden = true;
      if (q.continue) q.continue.hidden = true;
      q.add.hidden = false;
      q.qty.value = 1;
      this.qvAdded = false;
      this.picks.clear();
      this.setButtonState(q.add, 'idle');

      this.pickList = this.bundleCandidates(product);
      this.renderBundle();
      this.updateVariantUI();
    }

    familyHandles(family) {
      this.families = this.families || new Map();
      if (this.families.has(family)) return this.families.get(family);
      const seen = new Set();
      const handles = [];
      this.root.querySelectorAll('[data-sa-card]').forEach((card) => {
        const handle = card.dataset.handle;
        if (seen.has(handle)) return;
        seen.add(handle);
        const d = this.getData(handle);
        if (d && titleParts(d.title).family === family) handles.push(handle);
      });
      // Available colorways first, page order otherwise
      handles.sort((a, b) => Number(!this.getData(a).available) - Number(!this.getData(b).available));
      this.families.set(family, handles);
      return handles;
    }

    selectOption(button) {
      const product = this.qvProduct;
      if (!product) return;
      const index = Number(button.dataset.saOpt);
      const value = button.dataset.value;
      const current = this.qvVariant ? this.qvVariant.options.slice() : [];
      current[index] = value;
      const exact = product.variants.find((v) => v.options.every((o, i) => o === current[i]));
      const fallback =
        product.variants.find((v) => v.options[index] === value && v.available) ||
        product.variants.find((v) => v.options[index] === value);
      this.qvVariant = exact || fallback || this.qvVariant;
      this.updateVariantUI();
    }

    updateVariantUI() {
      const q = this.qv;
      const product = this.qvProduct;
      const variant = this.qvVariant;
      if (!product) return;

      const price = variant ? variant.price : product.price;
      const compare = variant ? variant.compare : product.compare;
      const onSale = compare > price;
      q.price.innerHTML = `<span${onSale ? ' class="is-sale"' : ''}>${escapeHtml(this.format(price))}</span>${
        onSale ? `<s>${escapeHtml(this.format(compare))}</s>` : ''
      }`;

      const qty = variant?.qty;
      if (variant?.available && typeof qty === 'number' && qty > 0 && qty <= 5) {
        q.stock.hidden = false;
        q.stock.textContent = `Only ${qty} left`;
      } else {
        q.stock.hidden = true;
      }

      q.options.querySelectorAll('[data-sa-opt]').forEach((btn) => {
        const oi = Number(btn.dataset.saOpt);
        const selected = variant && variant.options[oi] === btn.dataset.value;
        btn.setAttribute('aria-pressed', String(Boolean(selected)));
        const options = variant ? variant.options.slice() : [];
        options[oi] = btn.dataset.value;
        const match = product.variants.find((v) => v.options.every((o, i) => o === options[i]));
        btn.classList.toggle('is-soldout', !match || !match.available);
      });
      q.options.querySelectorAll('[data-sa-opt-name]').forEach((el) => {
        el.textContent = variant ? variant.options[Number(el.dataset.saOptName)] || '' : '';
      });

      this.updateCta();
    }

    /* Main button reflects everything that will be added: this product × qty + selected bundle picks. */
    updateCta() {
      const q = this.qv;
      const variant = this.qvVariant;
      const available = Boolean(variant && variant.available);
      q.add.disabled = !available;
      if (!q.add.classList.contains('is-busy') && !q.add.classList.contains('is-done')) {
        if (!available) {
          q.addLabel.textContent = 'Sold out';
        } else {
          const qty = clamp(parseInt(q.qty.value, 10) || 1, 1, 99);
          const count = qty + this.picks.size;
          const label = this.picks.size ? `Add ${count} to cart` : 'Add to cart';
          q.addLabel.textContent = `${label} · ${this.format(this.pendingTotal())}`;
        }
      }
      this.renderMeter();
      this.syncExpress();
    }

    pendingTotal() {
      if (this.qvAdded) return 0;
      const qty = clamp(parseInt(this.qv.qty.value, 10) || 1, 1, 99);
      let total = (this.qvVariant?.available ? this.qvVariant.price : 0) * qty;
      this.picks.forEach((pick) => {
        total += pick.price;
      });
      return total;
    }

    renderMeter() {
      const q = this.qv;
      if (!this.freeShip) {
        q.meter.hidden = true;
        return;
      }
      const reward = this.cfg.reward || 'free shipping';
      const cartTotal = this.cart ? this.cart.total_price : 0;
      const itemTotal = this.pendingTotal();
      const projected = cartTotal + itemTotal;
      let text;
      if (cartTotal >= this.freeShip) {
        text = `<b>${escapeHtml(capitalize(reward))}</b> unlocked`;
      } else if (projected >= this.freeShip && itemTotal > 0) {
        text = `This unlocks <b>${escapeHtml(reward)}</b>`;
      } else if (itemTotal > 0) {
        text = `Add this and you're <b>${escapeHtml(this.format(this.freeShip - projected))}</b> from ${escapeHtml(reward)}`;
      } else {
        text = `<b>${escapeHtml(this.format(this.freeShip - projected))}</b> away from ${escapeHtml(reward)}`;
      }
      q.meterText.innerHTML = text;
      q.meterFill.style.setProperty('--p', String(clamp(projected / this.freeShip, 0, 1)));
      q.meter.hidden = false;
    }

    bundleCandidates(product) {
      const handles = Array.isArray(this.cfg.bundle) ? this.cfg.bundle : [];
      if (!product || !handles.length) return [];
      const inCart = new Set((this.cart?.items || []).map((item) => item.handle));
      const family = titleParts(product.title).family;
      return handles
        .map((h) => this.getData(h))
        .filter((d) => d && d.handle !== product.handle && d.available && d.variants.length === 1 && d.variants[0].available && !inCart.has(d.handle))
        .map((d, i) => ({ d, i, same: titleParts(d.title).family === family }))
        .sort((a, b) => Number(a.same) - Number(b.same) || a.i - b.i)
        .slice(0, 3)
        .map(({ d }) => d);
    }

    renderBundle() {
      const q = this.qv;
      const picks = this.pickList;
      if (!picks.length) {
        q.bundle.hidden = true;
        q.bundleList.innerHTML = '';
        return;
      }
      const inCart = new Set((this.cart?.items || []).map((item) => item.handle));
      q.bundleList.innerHTML = picks
        .map((d) => {
          const variantId = d.variants[0].id;
          const owned = this.qvAdded && inCart.has(d.handle);
          const selected = this.picks.has(variantId);
          const thumb = d.images[0] ? imgUrl(d.images[0], 140) : '';
          const label = owned ? 'In cart ✓' : selected ? 'Added ✓' : '+ Add';
          return `<div class="sa-bundle__item${selected || owned ? ' is-selected' : ''}">
            <button type="button" class="sa-bundle__thumb" data-sa-bundle-view="${escapeHtml(d.handle)}" aria-label="View ${escapeHtml(d.title)}">${thumb ? `<img src="${escapeHtml(thumb)}" alt="" loading="lazy" decoding="async" width="56" height="56">` : ''}</button>
            <button type="button" class="sa-bundle__meta" data-sa-bundle-view="${escapeHtml(d.handle)}">
              <span class="sa-bundle__name">${escapeHtml(d.title)}</span>
              <span class="sa-bundle__price">${escapeHtml(this.format(d.price))}</span>
            </button>
            <button type="button" class="sa-btn sa-btn--line sa-bundle__toggle" data-sa-bundle-toggle="${variantId}" data-handle="${escapeHtml(d.handle)}" aria-pressed="${selected || owned}"${owned ? ' disabled' : ''} aria-label="${selected ? 'Remove' : 'Add'} ${escapeHtml(d.title)}"><span>${label}</span></button>
          </div>`;
        })
        .join('');
      q.bundle.hidden = false;
    }

    /* Before adding: picks join the main button. After adding: picks go straight into the cart. */
    toggleBundlePick(button) {
      const variantId = Number(button.dataset.saBundleToggle);
      const data = this.getData(button.dataset.handle);
      if (!variantId || !data || button.disabled || button.classList.contains('is-busy')) return;

      if (!this.qvAdded) {
        if (this.picks.has(variantId)) this.picks.delete(variantId);
        else this.picks.set(variantId, { handle: data.handle, price: data.variants[0].price, title: data.title });
        this.renderBundle();
        this.updateCta();
        return;
      }

      this.setButtonState(button, 'busy');
      this.addToCart([{ id: variantId, quantity: 1 }])
        .then(() => {
          this.announce(`${data.title} added to cart`);
          this.track('ark_shop_all_quick_add', { handle: data.handle, variantId, source: 'bundle' });
          this.renderBundle();
          this.renderMeter();
        })
        .catch((error) => this.setButtonState(button, 'error', error.userMessage || 'Unavailable'));
    }

    addFromQuickView() {
      const q = this.qv;
      const variant = this.qvVariant;
      if (!variant || !variant.available || q.add.classList.contains('is-busy')) return;
      const quantity = clamp(parseInt(q.qty.value, 10) || 1, 1, 99);
      const items = [{ id: variant.id, quantity }];
      this.picks.forEach((pick, id) => items.push({ id, quantity: 1 }));
      q.error.hidden = true;
      this.setButtonState(q.add, 'busy');
      this.addToCart(items)
        .then(() => {
          this.qvAdded = true;
          this.picks.clear();
          this.setButtonState(q.add, 'idle');
          q.foot.classList.add('is-added');
          q.add.hidden = true;
          q.checkout.hidden = false;
          if (q.continue) q.continue.hidden = false;
          const count = items.reduce((sum, item) => sum + item.quantity, 0);
          this.announce(`${count} ${count === 1 ? 'item' : 'items'} added to cart`);
          this.track('ark_shop_all_quick_add', {
            handle: this.qvProduct.handle,
            variantId: variant.id,
            quantity,
            bundleItems: items.length - 1,
            source: 'quick_view',
          });
          this.renderBundle();
          this.renderMeter();
          this.syncExpress();
        })
        .catch((error) => {
          this.setButtonState(q.add, 'idle');
          this.updateCta();
          q.error.textContent = error.fullMessage || 'Could not add to cart. Please try again.';
          q.error.hidden = false;
        });
    }

    bindDrag() {
      const handle = this.qv.dialog.querySelector('[data-sa-qv-drag]');
      const panel = this.qv.panel;
      if (!handle) return;
      let start = null;
      const { signal } = this.abort;

      handle.addEventListener('pointerdown', (event) => {
        if (isWideQuickView()) return;
        start = { y: event.clientY, t: performance.now(), dy: 0 };
        handle.setPointerCapture(event.pointerId);
        panel.style.transition = 'none';
      }, { signal });

      handle.addEventListener('pointermove', (event) => {
        if (!start) return;
        start.dy = Math.max(0, event.clientY - start.y);
        panel.style.transform = `translateY(${start.dy}px)`;
      }, { signal });

      const end = () => {
        if (!start) return;
        const { dy, t } = start;
        const velocity = dy / Math.max(1, performance.now() - t);
        start = null;
        panel.style.transition = '';
        if (dy > 110 || (dy > 30 && velocity > 0.6)) {
          this.closeQuickView();
        } else {
          panel.style.transition = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)';
          panel.style.transform = '';
          setTimeout(() => (panel.style.transition = ''), 320);
        }
      };
      handle.addEventListener('pointerup', end, { signal });
      handle.addEventListener('pointercancel', end, { signal });
    }

    syncCounter() {
      const track = this.qv.track;
      const total = track.children.length;
      const index = clamp(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)), 0, Math.max(0, total - 1));
      this.qv.counter.textContent = total ? `${index + 1} / ${total}` : '';
      [index - 1, index + 1, index + 2].forEach((i) => {
        const img = track.children[i]?.querySelector('img[data-src]');
        if (!img) return;
        img.srcset = img.dataset.srcset || '';
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      });
    }

    /* ---------- express checkout ---------- */

    /*
     * Wallet buttons must pay for exactly what the shopper chose:
     *  - nothing in the cart and no bundle picks: this product's Shop Pay / Apple Pay ("buy it now")
     *  - after adding (or with items already in the cart): whole-cart wallet buttons
     *  - bundle picks selected but not added yet: hidden until they add
     */
    syncExpress() {
      const box = this.qv.express;
      if (!box || !this.cfg.express || !this.qvOpen) return;
      if (this.qvAdded) {
        this.loadCartExpress();
        return;
      }
      const cartHasItems = Boolean(this.cart && this.cart.item_count > 0);
      if (cartHasItems || this.picks.size > 0) {
        this.expressKey = 'none';
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      this.loadExpress(this.qvProduct, this.qvVariant);
    }

    async loadCartExpress() {
      const box = this.qv.express;
      if (!box || !this.cart || !this.cart.item_count) return;
      const key = `cart:${this.cart.item_count}:${this.cart.total_price}`;
      if (this.expressKey === key && !box.hidden && box.innerHTML) return;
      this.expressKey = key;
      box.hidden = false;
      box.innerHTML = '';
      box.classList.add('is-loading');
      let html = '';
      try {
        const root = (this.cfg.routes.root || '/').replace(/\/?$/, '/');
        const res = await fetch(`${root}cart?section_id=ark-shop-all-cart-express`, { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (res.ok) {
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const wrap = doc.querySelector('[data-sa-cart-express]');
          html = wrap ? wrap.innerHTML : '';
        }
      } catch (error) {
        html = '';
      } finally {
        box.classList.remove('is-loading');
      }
      if (this.expressKey !== key) return;
      if (!html.trim()) {
        box.hidden = true;
        return;
      }
      box.innerHTML = html;
      try {
        window.Shopify?.PaymentButton?.init?.();
      } catch (error) {
        /* wallet buttons are best effort */
      }
    }

    /* Fetch the product's own Shop Pay / wallet buttons (same markup as the product page). */
    async loadExpress(product, variant) {
      const box = this.qv.express;
      if (!box || !this.cfg.express) return;
      if (!product || !variant || !variant.available) {
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      const key = `${product.handle}:${variant.id}`;
      this.expressKey = key;
      box.hidden = false;
      let html = this.expressCache.get(key);
      if (html === undefined) {
        box.innerHTML = '';
        box.classList.add('is-loading');
        try {
          const root = (this.cfg.routes.root || '/').replace(/\/?$/, '/');
          const res = await fetch(`${root}products/${encodeURIComponent(product.handle)}?variant=${variant.id}&section_id=ark-shop-all-buy`, {
            headers: { Accept: 'text/html' },
          });
          if (!res.ok) throw new Error('Express checkout unavailable');
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const form = doc.querySelector('form');
          html = form && form.querySelector('shopify-accelerated-checkout, .shopify-payment-button') ? form.outerHTML : '';
        } catch (error) {
          html = '';
        } finally {
          box.classList.remove('is-loading');
        }
        this.expressCache.set(key, html);
      }
      if (this.expressKey !== key) return; // shopper already moved to another product
      if (!html) {
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      box.innerHTML = html;
      this.syncExpressQuantity();
      try {
        window.Shopify?.PaymentButton?.init?.();
      } catch (error) {
        /* wallet buttons are best effort */
      }
    }

    syncExpressQuantity() {
      const input = this.qv.express?.querySelector('input[name="quantity"]');
      if (input) input.value = clamp(parseInt(this.qv.qty.value, 10) || 1, 1, 99);
    }

    /* ---------- product view tracking ---------- */

    marketingAllowed() {
      const privacy = window.Shopify?.customerPrivacy;
      if (privacy && typeof privacy.marketingAllowed === 'function') {
        try {
          return privacy.marketingAllowed();
        } catch (error) {
          return true;
        }
      }
      return true;
    }

    categoryLabels(handle) {
      const card = this.root.querySelector(`[data-sa-card][data-handle="${CSS.escape(handle)}"]`);
      const slugs = (card?.dataset.cats || '').split(' ').filter(Boolean);
      return slugs
        .map((slug) => this.root.querySelector(`.sa-chip[data-sa-chip="${CSS.escape(slug)}"] .sa-chip__label`)?.textContent.trim())
        .filter(Boolean);
    }

    /* A quick view is a product view: send what the product page sends (Meta, Klaviyo, theme search). */
    reportProductView(product) {
      if (!product) return;
      try {
        const id = String(product.id);
        const recent = JSON.parse(localStorage.getItem('viewedProducts') || '[]').filter((entry) => entry !== id);
        recent.unshift(id);
        localStorage.setItem('viewedProducts', JSON.stringify(recent.slice(0, 4)));
      } catch (error) {
        /* ignore storage errors */
      }

      if (!this.cfg.trackViews || this.viewed.has(product.handle) || !this.marketingAllowed()) return;
      this.viewed.add(product.handle);

      const variant = product.variants.find((v) => v.available) || product.variants[0] || null;
      const cents = variant ? variant.price : product.price;
      const value = cents / 100;
      const currency = this.cfg.currency || 'USD';

      // Meta: same ViewContent fields the Facebook & Instagram app sends on product pages (catalog match = product ID).
      try {
        const pixels = typeof window.fbq?.getState === 'function' ? window.fbq.getState().pixels || [] : [];
        pixels.forEach((pixel) => {
          window.fbq(
            'trackSingle',
            pixel.id,
            'ViewContent',
            {
              content_ids: [product.id],
              content_type: 'product_group',
              content_name: product.title,
              content_category: product.type || '',
              currency,
              value,
              contents: [{ id: product.id, quantity: 1, item_price: value }],
            },
            { eventID: `sa-qv-${product.id}-${Date.now()}` }
          );
        });
      } catch (error) {
        /* tracking is best effort */
      }

      // Klaviyo: Viewed Product (browse abandonment) + recently viewed items.
      try {
        const klaviyo = window.klaviyo && typeof window.klaviyo.push === 'function' ? window.klaviyo : (window._learnq = window._learnq || []);
        const item = {
          Name: product.title,
          ProductID: product.id,
          Categories: this.categoryLabels(product.handle),
          ImageURL: product.images[0] ? new URL(imgUrl(product.images[0], 600), window.location.href).href : '',
          URL: new URL(product.url, window.location.origin).href,
          Brand: product.vendor,
          Price: this.format(cents),
          Value: value.toFixed(2),
          CompareAtPrice: this.format(variant ? variant.compare : product.compare),
        };
        klaviyo.push(['track', 'Viewed Product', item]);
        klaviyo.push([
          'trackViewedItem',
          {
            Title: item.Name,
            ItemId: item.ProductID,
            Categories: item.Categories,
            ImageUrl: item.ImageURL,
            Url: item.URL,
            Metadata: { Brand: item.Brand, Price: item.Price, Value: item.Value, CompareAtPrice: item.CompareAtPrice },
          },
        ]);
      } catch (error) {
        /* tracking is best effort */
      }
    }

    /* ---------- cart ---------- */

    openCart() {
      if (typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
        return;
      }
      const drawer = Array.from(document.querySelectorAll('cart-drawer-component')).find((el) => el.querySelector('dialog'));
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = (this.cfg.routes.cart || '/cart.js').replace(/\.js$/, '');
    }

    setButtonState(button, state, text, resetAfter = 1600, resetText) {
      if (!button) return;
      const label = button.querySelector('span') || button;
      if (!button.dataset.saLabel) button.dataset.saLabel = label.textContent;
      clearTimeout(button.saTimer);
      button.classList.remove('is-busy', 'is-done', 'is-error');
      if (state === 'busy') {
        button.classList.add('is-busy');
        button.setAttribute('aria-busy', 'true');
        return;
      }
      button.removeAttribute('aria-busy');
      if (state === 'idle') {
        if (button === this.qv.add) button.classList.remove('sa-btn--line');
        return;
      }
      button.classList.add(state === 'done' ? 'is-done' : 'is-error');
      label.textContent = state === 'done' ? `${text} ✓` : text;
      if (resetAfter) {
        button.saTimer = setTimeout(() => {
          button.classList.remove('is-done', 'is-error');
          label.textContent = resetText || button.dataset.saLabel;
        }, resetAfter);
      }
    }

    async addToCart(items) {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId);
      const body = { items };
      if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

      this.pendingAdds += 1;
      let res;
      let json;
      try {
        res = await fetch(this.cfg.routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        json = await res.json().catch(() => ({}));
      } finally {
        this.pendingAdds -= 1;
      }
      if (!res.ok || json.status) {
        const message = String(json.description || json.message || '');
        const error = new Error(message || 'Add to cart failed');
        if (/can.t add more|already have|in your cart/i.test(message)) error.userMessage = 'Max in cart';
        else if (/sold out|out of stock|unavailable/i.test(message)) error.userMessage = 'Sold out';
        else error.userMessage = 'Unavailable';
        error.fullMessage = message;
        throw error;
      }

      const cart = await this.refreshCart({ bump: true });
      try {
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
      } catch (error) {
        /* cart app sync is best effort */
      }
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'ark-shop-all',
            data: {
              source: 'ark-shop-all',
              itemCount: cart ? cart.item_count : 0,
              sections: json.sections,
            },
          },
        })
      );
      return cart;
    }

    async refreshCart({ bump = false, notifyTheme = false } = {}) {
      const before = this.cart ? this.cart.item_count : null;
      try {
        const res = await fetch(this.cfg.routes.cart || '/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        this.cart = await res.json();
      } catch (error) {
        return this.cart;
      }
      if (notifyTheme && before !== null && before !== this.cart.item_count) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: this.cart, sourceId: 'ark-shop-all', data: { source: 'ark-shop-all', itemCount: this.cart.item_count } },
          })
        );
      }
      this.renderBar(bump);
      if (this.qvOpen) {
        this.renderMeter();
        this.syncExpress();
      }
      return this.cart;
    }

    renderBar(bump) {
      const bar = this.bar;
      const cart = this.cart;
      if (!bar || !cart) return;
      const count = cart.item_count || 0;
      const visible = count > 0;
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sa-bar-on', visible);
      if (!visible) return;

      bar.querySelector('[data-sa-bar-count]').textContent = String(count);
      bar.querySelector('[data-sa-bar-total]').textContent = this.format(cart.total_price);
      const note = bar.querySelector('[data-sa-bar-note]');
      const fill = bar.querySelector('[data-sa-bar-fill]');
      const reward = this.cfg.rewardShort || this.cfg.reward || 'free shipping';
      if (this.freeShip) {
        const remaining = this.freeShip - cart.total_price;
        note.textContent = remaining > 0 ? `${this.format(remaining)} to ${reward}` : `${capitalize(reward)} ✓`;
        fill.style.setProperty('--p', String(clamp(cart.total_price / this.freeShip, 0, 1)));
      } else {
        note.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
        fill.style.setProperty('--p', '0');
      }
      if (bump) {
        const bag = bar.querySelector('.sa-bar__bag');
        bag.classList.remove('is-bump');
        void bag.offsetWidth;
        bag.classList.add('is-bump');
      }
    }
  }

  /* ---------- boot ---------- */

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-sa-root]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new ShopAll(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-sa-root]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
